import React, { useState, useEffect } from 'react';
import { bool, func, object, number, string } from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage, intlShape } from '../../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { propTypes } from '../../../../util/types';
import {
  Avatar,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
} from '../../../../components';

import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';
import CustomLinksMenu from './CustomLinksMenu/CustomLinksMenu';

import css from './TopbarDesktop.module.css';
import { DashboadPageNavOption } from '../../../../util/enums';
import { isTrailerRenter } from '../../../../util/genericHelpers';
import { isHaulStarMember, isPausedMember } from '../../../../util/subscriptionHelpers';
import { createBillingPortalSessionAPI } from '../../../../util/api';

const SignupLink = ({ currentPage }) => {
  const classes = classNames(css.topbarLink, {
    [css.topbarLinkActive]: currentPage === 'SignupPage',
  });
  return (
    <NamedLink name="SignupPage" className={classes}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.signup" />
      </span>
    </NamedLink>
  );
};

const LoginLink = ({ currentPage }) => {
  const classes = classNames(css.topbarLink, {
    [css.topbarLinkActive]: currentPage === 'LoginPage',
  });
  return (
    <NamedLink name="LoginPage" className={classes}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
    </NamedLink>
  );
};

const BlogsLink = ({ currentPage }) => {
  const classes = classNames(css.topbarLink, {
    [css.topbarLinkActive]: currentPage === 'BlogPage',
  });
  return (
    <NamedLink name="BlogPage" className={classes}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.blog" />
      </span>
    </NamedLink>
  );
};

const WorkshopLink = ({ currentPage }) => {
  const classes = classNames(css.topbarLink, {
    [css.topbarLinkActive]:
      currentPage === 'WorkshopPage' ||
      currentPage === 'WorkshopTypePage' ||
      currentPage === 'WorkshopProductPage',
  });
  return (
    <NamedLink name="WorkshopPage" className={classes}>
      <span className={css.topbarLinkLabel}>The Workshop</span>
    </NamedLink>
  );
};

const ToolsLink = ({ currentPage }) => {
  const classes = classNames(css.topbarLink, {
    [css.topbarLinkActive]:
      currentPage === 'ToolsIndexPage' ||
      currentPage === 'TowingCapacityPage' ||
      currentPage === 'TrailerPayloadPage' ||
      currentPage === 'SavingsCalculatorPage' ||
      currentPage === 'QuizPage',
  });
  return (
    <NamedLink name="ToolsIndexPage" className={classes}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.tools" />
      </span>
    </NamedLink>
  );
};

const FAQLink = ({ currentPage }) => {
  const classes = classNames(css.topbarLink, {
    [css.topbarLinkActive]: currentPage === 'FAQPage',
  });
  return (
    <NamedLink name="FAQPage" className={classes}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.faqLink" />
      </span>
    </NamedLink>
  );
};

const ContactLink = ({ currentPage }) => {
  const classes = classNames(css.topbarLink, {
    [css.topbarLinkActive]: currentPage === 'ContactUsPage',
  });
  return (
    <NamedLink name="ContactUsPage" className={classes}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.contact" />
      </span>
    </NamedLink>
  );
};

const CategoriesLink = ({ currentPage }) => {
  const classes = classNames(css.topbarLink, {
    [css.topbarLinkActive]: currentPage === 'SeoCategoriesPage',
  });
  return (
    <NamedLink name="SeoCategoriesPage" className={classes}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.categories" />
      </span>
    </NamedLink>
  );
};

const InboxLink = ({
  notificationCount,
  hasUnreadMessages,
  currentUserHasListings,
  currentPage,
}) => {
  const notificationDot =
    notificationCount > 0 || hasUnreadMessages ? <div className={css.notificationDot} /> : null;
  const classes = classNames(css.topbarLink, {
    [css.topbarLinkActive]: currentPage.includes('InboxPage'),
  });
  return (
    <NamedLink
      className={classes}
      name="InboxPage"
      params={{ tab: currentUserHasListings ? 'sales' : 'orders' }}
    >
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.inbox" />
        {notificationDot}
      </span>
    </NamedLink>
  );
};

const ProfileMenu = ({ currentPage, currentUser, onLogout, currentUserHasListings }) => {
  const [portalLoading, setPortalLoading] = useState(false);
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  const isMember = isHaulStarMember(currentUser);
  // A paused member is still fundamentally a member — they must be able to reach
  // the membership page to resume. `hasMembership` covers active + paused so the
  // menu shows "Manage membership" (not the upgrade CTA) for paused owners too.
  const hasMembership = isMember || isPausedMember(currentUser);
  // Confirmed renters don't get owner-only surfaces (listings, dashboard, Haul Star).
  // Owners + untagged users are treated as owners (safe default).
  const isRenter = isTrailerRenter(currentUser);

  const handleManageBilling = async e => {
    if (e && e.preventDefault) e.preventDefault();
    if (portalLoading) return;
    setPortalLoading(true);
    try {
      const res = await createBillingPortalSessionAPI();
      const url = res && (res.url || res.data?.url);
      if (url) {
        window.location.assign(url);
      } else {
        setPortalLoading(false);
      }
    } catch (err) {
      // Fall back to the sales page so the click is never a dead end.
      setPortalLoading(false);
      if (typeof window !== 'undefined') {
        window.location.assign('/subscription');
      }
    }
  };

  // ⚠️ MenuContent validates EVERY child with `child.type !== MenuItem` via
  // React.Children.forEach, which DOES invoke the callback for null children.
  // Passing `{cond ? null : <MenuItem/>}` therefore throws
  // "Cannot read properties of null (reading 'type')" DURING RENDER — which,
  // when it happens inside a redux store-notify (login flow), loops React's
  // error recovery into "Maximum update depth exceeded" and freezes the page.
  // So: build the children as a pre-filtered ARRAY, never inline nulls.
  const ownerOnlyItems = isRenter
    ? []
    : [
        <MenuItem key="ManageListingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ManageListingsPage'))}
            name="ManageListingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.yourListingsLink" />
          </NamedLink>
        </MenuItem>,
        <MenuItem key="UserDashboardPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('UserDashboardPage'))}
            name="UserDashboardPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.userDashboardLink" />
          </NamedLink>
        </MenuItem>,
        <MenuItem key="ToolboxIndexPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ToolboxIndexPage'))}
            name="ToolboxIndexPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.toolboxLink" />
          </NamedLink>
        </MenuItem>,
        <MenuItem key="SubscriptionPage">
          {hasMembership ? (
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('MembershipPage'))}
              name="MembershipPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.manageMembershipLink" />
            </NamedLink>
          ) : (
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('SubscriptionPage'))}
              name="SubscriptionPage"
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.getHaulStarLink" />
            </NamedLink>
          )}
        </MenuItem>,
      ];

  const commonItems = [
    <MenuItem key="ProfileSettingsPage">
      <NamedLink
        className={classNames(css.menuLink, currentPageClass('ProfileSettingsPage'))}
        name="ProfileSettingsPage"
      >
        <span className={css.menuItemBorder} />
        <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
      </NamedLink>
    </MenuItem>,
    <MenuItem key="AccountSettingsPage">
      <NamedLink
        className={classNames(css.menuLink, currentPageClass('AccountSettingsPage'))}
        name="AccountSettingsPage"
      >
        <span className={css.menuItemBorder} />
        <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
      </NamedLink>
    </MenuItem>,
    <MenuItem key="logout">
      <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
        <span className={css.menuItemBorder} />
        <FormattedMessage id="TopbarDesktop.logout" />
      </InlineTextButton>
    </MenuItem>,
  ];

  return (
    <Menu>
      <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
        <Avatar className={css.avatar} user={currentUser} disableProfileLink />
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        {[...ownerOnlyItems, ...commonItems]}
      </MenuContent>
    </Menu>
  );
};

const TopbarDesktop = props => {
  const {
    className,
    config,
    customLinks,
    currentUser,
    currentPage,
    rootClassName,
    currentUserHasListings,
    notificationCount,
    hasUnreadMessages,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues,
  } = props;
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const marketplaceName = config.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const giveSpaceForSearch = customLinks == null || customLinks?.length === 0;
  const classes = classNames(rootClassName || css.root, className);

  // Confirmed renters don't get the "Post a new listing" primary link.
  // Owners + untagged users are treated as owners (safe default).
  const isRenterUser = authenticatedOnClientSide && isTrailerRenter(currentUser);

  const inboxLinkMaybe = authenticatedOnClientSide ? (
    <InboxLink
      notificationCount={notificationCount}
      hasUnreadMessages={hasUnreadMessages}
      currentUserHasListings={currentUserHasListings}
      currentPage={currentPage}
    />
  ) : null;

  const profileMenuMaybe = authenticatedOnClientSide ? (
    <ProfileMenu
      currentPage={currentPage}
      currentUser={currentUser}
      onLogout={onLogout}
      currentUserHasListings={currentUserHasListings}
    />
  ) : null;

  const signupLinkMaybe = isAuthenticatedOrJustHydrated ? null : (
    <SignupLink currentPage={currentPage} />
  );
  const loginLinkMaybe = isAuthenticatedOrJustHydrated ? null : (
    <LoginLink currentPage={currentPage} />
  );

  return (
    <nav className={classes}>
      <div className={css.leftLinks}>
        <LinkedLogo
          className={css.logoLink}
          layout="desktop"
          alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
          linkToExternalSite={config?.topbar?.logoLink}
        />
        <TopbarSearchForm
          className={classNames(css.searchLink, { [css.takeAvailableSpace]: giveSpaceForSearch })}
          desktopInputRoot={css.topbarSearchWithLeftPadding}
          onSubmit={onSearchSubmit}
          initialValues={initialSearchFormValues}
          appConfig={config}
        />
      </div>
      <div className={css.rightLinks}>
        <CustomLinksMenu
          currentPage={currentPage}
          customLinks={customLinks}
          intl={intl}
          hideCreateListing={isRenterUser}
          hasClientSideContentReady={authenticatedOnClientSide || !isAuthenticatedOrJustHydrated}
        />

        {inboxLinkMaybe}
        <WorkshopLink currentPage={currentPage} />
        <ToolsLink currentPage={currentPage} />
        <BlogsLink currentPage={currentPage} />
        <FAQLink currentPage={currentPage} />
        <ContactLink currentPage={currentPage} />
        <CategoriesLink currentPage={currentPage} />
        {profileMenuMaybe}
        {signupLinkMaybe}
        {loginLinkMaybe}
      </div>
    </nav>
  );
};

TopbarDesktop.defaultProps = {
  rootClassName: null,
  className: null,
  currentUser: null,
  currentPage: null,
  notificationCount: 0,
  hasUnreadMessages: false,
  initialSearchFormValues: {},
  config: null,
};

TopbarDesktop.propTypes = {
  rootClassName: string,
  className: string,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  isAuthenticated: bool.isRequired,
  onLogout: func.isRequired,
  notificationCount: number,
  hasUnreadMessages: bool,
  onSearchSubmit: func.isRequired,
  initialSearchFormValues: object,
  intl: intlShape.isRequired,
  config: object,
};

export default TopbarDesktop;
